import axios from 'axios';
import type {
  TenantContextResponse,
  TenantSwitchRequest,
  TenantSummary,
  TenantSampleItem,
  TenantSampleCreateRequest,
  RoutingStatusResponse,
  PageResponse,
  SampleSearchCondition,
} from '../types/tenantSwitcher';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — Bearer 토큰 + X-Tenant-Id 헤더 부착
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // X-Tenant-Id: 세션 스토리지에서 현재 테넌트 읽기 (POST /context 후 갱신됨)
  const tenantId =
    sessionStorage.getItem('x_tenant_id') ??
    localStorage.getItem('x_tenant_id');
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  return config;
});

// Response interceptor — ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const tenantSwitcherApi = {
  /** GET /api/eraf/tenant-switcher/context — 현재 테넌트 컨텍스트 (TENANT_READ) */
  getContext: (): Promise<TenantContextResponse> =>
    apiClient
      .get<unknown, { data: TenantContextResponse }>('/tenant-switcher/context')
      .then((res) => res.data),

  /** POST /api/eraf/tenant-switcher/context — 테넌트 전환 (TENANT_SWITCH) */
  switchTenant: (req: TenantSwitchRequest): Promise<TenantContextResponse> =>
    apiClient
      .post<unknown, { data: TenantContextResponse; headers: Record<string, string> }>(
        '/tenant-switcher/context',
        req,
        { transformResponse: undefined }
      )
      .then((res) => {
        // 응답 헤더의 X-Tenant-Id 를 세션 스토리지에 저장 — 이후 요청에 부착
        const newTenantId = (res as unknown as { headers?: Record<string, string> }).headers?.[
          'x-tenant-id'
        ];
        if (newTenantId) {
          sessionStorage.setItem('x_tenant_id', newTenantId);
          localStorage.setItem('x_tenant_id', newTenantId);
        } else {
          // 헤더가 없어도 요청 값으로 클라이언트 측 갱신
          sessionStorage.setItem('x_tenant_id', req.tenantId);
          localStorage.setItem('x_tenant_id', req.tenantId);
        }
        return (res as unknown as { data: TenantContextResponse }).data;
      }),

  /** GET /api/eraf/tenant-switcher/tenants — 전환 가능 테넌트 목록 (TENANT_READ) */
  getTenants: (): Promise<TenantSummary[]> =>
    apiClient
      .get<unknown, { data: TenantSummary[] }>('/tenant-switcher/tenants')
      .then((res) => res.data),

  /** GET /api/eraf/tenant-switcher/samples — 테넌트 격리 표본 목록 (TENANT_READ) */
  listSamples: (params: SampleSearchCondition): Promise<PageResponse<TenantSampleItem>> => {
    const sp = new URLSearchParams();
    if (params.category) sp.set('category', params.category);
    if (params.keyword) sp.set('keyword', params.keyword);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<TenantSampleItem> }>(
        `/tenant-switcher/samples?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** POST /api/eraf/tenant-switcher/samples — 표본 생성 (TENANT_SWITCH · WRITE 라우팅) */
  createSample: (req: TenantSampleCreateRequest): Promise<TenantSampleItem> =>
    apiClient
      .post<unknown, { data: TenantSampleItem }>('/tenant-switcher/samples', req)
      .then((res) => res.data),

  /** GET /api/eraf/tenant-switcher/routing — 읽기/쓰기 라우팅 상태 (TENANT_READ) */
  getRoutingStatus: (): Promise<RoutingStatusResponse> =>
    apiClient
      .get<unknown, { data: RoutingStatusResponse }>('/tenant-switcher/routing')
      .then((res) => res.data),
};
